import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Draggable from "react-draggable";
import { styled } from "@mui/material/styles";
import { propOr } from "ramda";

import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Clear";
import Paper from "@mui/material/Paper";

import AnnotationeerControls from "~/components/AnnotationeerControls";
import NotesModal from "~/components/Modal/NotesModal";
import Viewer from "~/components/Viewer";
import ZoomController from "~/components/ZoomController";
import Pagination from "~/components/Pagination";
import Pager from "~/components/Pager";
import InlineToolbar from "~/components/InlineToolbar";
import MaterialOutlineOverlay from "~/components/MaterialOutlineOverlay";

import usePaging from "~/hooks/usePaging";

import generateTeacherMaterialGetters from "~/libs/dls";
import EventBus from "~/libs/eventBus";
import PageStore from "~/libs/pageStore";

import { pdfToCssUnits } from "~/utils";
import { getToFormatter, getFromFormatter } from "~/utils/page";
import { getQuickDialActions } from "~/utils/quickdial";
import { selectShowModalCutout } from "~/store/ducks/user/uistate";
import { useSelector } from "react-redux";

const StyledAnnotationeerControls = styled(AnnotationeerControls)(() => ({
  marginLeft: "auto",
  marginRight: 0
}));

const Title = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  marginLeft: 0,
  marginRight: "auto"
}));

const Header = styled(DialogTitle)(() => ({
  position: "relative",
  margin: 0,
  padding: 0,
  display: "flex",
  alignItems: "center",
  borderBottom: "2px solid #3d3d3d"
}));

const Content = styled("div")(() => ({
  flex: 1
}));

const Wrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%"
}));

const StyledDialogContent = styled(DialogContent)`
  flex-basis: 0;
  padding: 0;
`;

const StyledPaper = styled(Paper)`
  display: flex;
  position: absolute;
  max-width: 600px;
  z-index: 1700;
  flex-direction: column;
`;

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

function ViewerModal({
  showModal,
  hideModal,
  activePages,
  addPresentationMarker,
  updatePresentationMarker,
  category,
  isPresentationMode,
  material,
  kind
}) {
  const showCutout = useSelector(selectShowModalCutout);
  material = material || {};

  const initialPage = PageStore.getCurrentPage(material._id);

  const DLS = generateTeacherMaterialGetters(material);
  const { size, pagesCount } = material;
  const name = propOr("", "name", material);
  const werk = propOr("", "Werk", material);

  const pageOffset = propOr(0, "pageOffset", material);

  useEffect(() => {
    EventBus.dispatch("zoomToFit", "modal", {
      direction: "top",
      padding: { top: 48 }
    });
  }, []);

  const isDoublePage = false;
  const pageWidth = pdfToCssUnits(size ? size.width : 0);
  const pageHeight = pdfToCssUnits(size ? size.height : 0);
  const totalPageWidth = pageWidth * (isDoublePage ? 2 : 1);
  const totalPageHeight = pageHeight;

  const preOffsetNumbering = propOr("roman", "preOffsetNumbering", material);
  const toFormat = getToFormatter(preOffsetNumbering);
  const fromFormat = getFromFormatter(preOffsetNumbering);

  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const { absolutePage, readablePage, goToPrevious, goToNext, gotToReadablePage } = usePaging(
    1,
    pagesCount,
    pageOffset,
    onPageChange,
    toFormat,
    fromFormat,
    initialPage
  );

  const modalPageMeta = useMemo(() => {
    return [
      {
        bookId: material._id,
        pages: [readablePage]
      }
    ];
  }, [material, readablePage]);

  const isLeftPagerVisible = absolutePage !== 1;
  const isRightPagerVisible = absolutePage !== pagesCount;

  function onPageChange(page) {
    PageStore.setCurrentPage(material._id, page);
    EventBus.dispatch("zoomToFit", "modal", {
      direction: "top",
      padding: { top: 48 }
    });
  }

  function openNotesDialog() {
    setNotesDialogOpen(true);
  }

  function closeNotesDialog() {
    setNotesDialogOpen(false);
  }

  function renderOutlineList() {
    if (material.inhaltsverzeichnis && material.inhaltsverzeichnis.length > 0) {
      return (
        <MaterialOutlineOverlay
          outline={material.inhaltsverzeichnis}
          goToPage={gotToReadablePage}
          page={readablePage}
        />
      );
    }
    return null;
  }

  function renderPagination() {
    return (
      <>
        <Pagination
          page={readablePage}
          goToPrevious={goToPrevious}
          goToNext={goToNext}
          goToDestination={gotToReadablePage}
          showGoToPrevious={isLeftPagerVisible}
          showGoToNext={isRightPagerVisible}
        />

        {isLeftPagerVisible && <Pager direction="left" onClick={goToPrevious} />}

        {isRightPagerVisible && <Pager direction="right" onClick={goToNext} />}
      </>
    );
  }

  function renderViewer(zoomController) {
    return (
      <Viewer
        name="modal"
        bookId={material._id}
        renderPagination={renderPagination}
        renderOutlineList={renderOutlineList}
        zoomController={zoomController}
        isDoublePage={isDoublePage}
        page={absolutePage}
        pages={[absolutePage]}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        totalPageWidth={totalPageWidth}
        totalPageHeight={totalPageHeight}
        getThumbnailPath={DLS.getThumbnailPath}
        getImagePath={DLS.getImagePath}
        showAnnotations
        showCutout={showCutout}
        isModalCutout
        showMaterials={false}
        quickDialActions={getQuickDialActions({
          material,
          showModal,
          zoomController,
          activePages,
          addPresentationMarker,
          updatePresentationMarker,
          category,
          printOrigin: "modal",
          isPresentationMode,
          openNotesDialog,
          currentPage: absolutePage
        })}
      />
    );
  }

  return (
    <>
      <Header className="dialog-title">
        <Title variant="h6" id={`${kind}-modal-title`}>
          {werk ? `${werk} – ` : ""}
          {name}
        </Title>

        <StyledAnnotationeerControls isDialogControls optionalToDeleteAnnotationMeta={modalPageMeta} />

        <InlineToolbar>
          <StyledIconButton onClick={hideModal} size="large" aria-label="Schließen">
            <CloseIcon />
          </StyledIconButton>
        </InlineToolbar>
      </Header>
      <StyledDialogContent>
        <Wrapper className="viewer-modal-wrapper">
          <Content className="viewer-modal-content">
            <ZoomController channel="modal">{renderViewer}</ZoomController>
          </Content>
        </Wrapper>
      </StyledDialogContent>
      {notesDialogOpen && (
        <Draggable cancel={'[class*="MuiDialogContent-root"]'} bounds="parent">
          <StyledPaper>
            <NotesModal
              noteId={material.note ? material.note._id : null}
              material={material}
              closeDialog={closeNotesDialog}
            />
          </StyledPaper>
        </Draggable>
      )}
    </>
  );
}

ViewerModal.propTypes = {
  showModal: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  activePages: PropTypes.array.isRequired,
  addPresentationMarker: PropTypes.func.isRequired,
  updatePresentationMarker: PropTypes.func.isRequired,
  category: PropTypes.object.isRequired,
  isPresentationMode: PropTypes.bool.isRequired,
  material: PropTypes.object,
  kind: PropTypes.string.isRequired
};

export default ViewerModal;
